import React, { useState, useCallback, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { DAYS, DAY_COLORS, ACTIVITY_TYPE_COLORS } from '../data/curriculumData';
import { AI_DAYS, AI_DAY_COLORS } from '../data/aiLiteracyCurriculumData';
import { PP_DAYS, PP_DAY_COLORS } from '../data/publicPresentationCurriculumData';
import { ESPORTS_DAYS, ESPORTS_DAY_COLORS } from '../data/esportsCurriculumData';
import { VEDIC_DAYS, VEDIC_DAY_COLORS } from '../data/vedicMathCurriculumData';
import { ROBOTICS_DAYS, ROBOTICS_DAY_COLORS } from '../data/roboticsCurriculumData';

const PROGRAMS = [
  {
    key: 'entrepreneurship',
    icon: '💡',
    labelEn: 'Kids Entrepreneurship',
    labelJa: 'キッズ・アントレプレナーシップ',
    days: DAYS,
    colors: DAY_COLORS,
    headerGradient: 'linear-gradient(135deg, #FF6B35, #FFD166)',
  },
  {
    key: 'ai-literacy',
    icon: '🤖',
    labelEn: 'AI Literacy',
    labelJa: 'AIリテラシー',
    days: AI_DAYS,
    colors: AI_DAY_COLORS,
    headerGradient: 'linear-gradient(135deg, #118AB2, #06D6A0)',
  },
  {
    key: 'public-presentation',
    icon: '🎤',
    labelEn: 'Public Presentation',
    labelJa: 'パブリックプレゼン',
    days: PP_DAYS,
    colors: PP_DAY_COLORS,
    headerGradient: 'linear-gradient(135deg, #A855F7, #EC4899)',
  },
  {
    key: 'esports',
    icon: '🎮',
    labelEn: 'E-Sports',
    labelJa: 'eスポーツ',
    days: ESPORTS_DAYS,
    colors: ESPORTS_DAY_COLORS,
    headerGradient: 'linear-gradient(135deg, #06B6D4, #118AB2)',
  },
  {
    key: 'vedic-mathematics',
    icon: '🔢',
    labelEn: 'Vedic Mathematics',
    labelJa: 'ヴェーダ数学',
    days: VEDIC_DAYS,
    colors: VEDIC_DAY_COLORS,
    headerGradient: 'linear-gradient(135deg, #F59E0B, #EF4444)',
  },
  {
    key: 'robotics',
    icon: '🦾',
    labelEn: 'Robotics',
    labelJa: 'ロボティクス',
    days: ROBOTICS_DAYS,
    colors: ROBOTICS_DAY_COLORS,
    headerGradient: 'linear-gradient(135deg, #10B981, #06B6D4)',
  },
];

// ─────────────────────────────────────────────────────
// VOCABULARY FLASHCARD (full-screen)
// ─────────────────────────────────────────────────────
function VocabFlashcard({ vocab, lang, onClose, color }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const card = vocab[idx];

  const next = useCallback(() => { setFlipped(false); setTimeout(() => setIdx(i => Math.min(i + 1, vocab.length - 1)), 150); }, [vocab.length]);
  const prev = useCallback(() => { setFlipped(false); setTimeout(() => setIdx(i => Math.max(i - 1, 0)), 150); }, []);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem' }}>
        {vocab.map((_, i) => (
          <div key={i} onClick={() => { setFlipped(false); setTimeout(() => setIdx(i), 150); }}
            style={{
              width: 12, height: 12, borderRadius: '50%', cursor: 'pointer',
              background: i === idx ? 'white' : 'rgba(255,255,255,0.3)',
              transition: 'background 0.2s',
            }} />
        ))}
      </div>

      {/* Card */}
      <div
        onClick={() => setFlipped(f => !f)}
        style={{
          width: '100%', maxWidth: 700,
          minHeight: '55vh',
          background: flipped
            ? 'linear-gradient(135deg, #1e293b, #334155)'
            : color,
          borderRadius: 32,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: 'clamp(2rem, 6vw, 4rem)',
          cursor: 'pointer',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          transition: 'background 0.4s',
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        {!flipped ? (
          <>
            <div style={{ fontSize: 'clamp(4rem, 15vw, 8rem)', lineHeight: 1, marginBottom: '1rem' }}>
              {card.emoji}
            </div>
            <div style={{
              fontSize: 'clamp(2.5rem, 8vw, 5rem)',
              fontWeight: 900,
              color: 'white',
              letterSpacing: '-0.02em',
              textShadow: '0 4px 20px rgba(0,0,0,0.2)',
              lineHeight: 1.1,
            }}>
              {card.word[lang]}
            </div>
            <div style={{ marginTop: '1.5rem', fontSize: 'clamp(0.85rem, 2.5vw, 1.1rem)', color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
              {lang === 'ja' ? 'タップして意味を見る' : 'Tap to see definition'}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 'clamp(2rem, 6vw, 3.5rem)', fontWeight: 900, color: '#94a3b8', marginBottom: '1rem', letterSpacing: '-0.01em' }}>
              {card.word[lang]}
            </div>
            <div style={{
              fontSize: 'clamp(1.4rem, 5vw, 2.8rem)',
              fontWeight: 800,
              color: 'white',
              lineHeight: 1.4,
              maxWidth: 560,
            }}>
              {card.def[lang]}
            </div>
            <div style={{ marginTop: '1.5rem', fontSize: 'clamp(0.85rem, 2.5vw, 1.1rem)', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
              {lang === 'ja' ? 'タップして単語に戻る' : 'Tap to go back'}
            </div>
          </>
        )}
      </div>

      {/* Nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginTop: '2rem' }}>
        <button
          onClick={prev} disabled={idx === 0}
          style={{
            width: 64, height: 64, borderRadius: '50%', border: 'none', cursor: idx === 0 ? 'not-allowed' : 'pointer',
            background: idx === 0 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
            color: 'white', fontSize: '1.5rem', fontWeight: 900,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >‹</button>

        <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, fontSize: '1rem', minWidth: 60, textAlign: 'center' }}>
          {idx + 1} / {vocab.length}
        </div>

        <button
          onClick={next} disabled={idx === vocab.length - 1}
          style={{
            width: 64, height: 64, borderRadius: '50%', border: 'none', cursor: idx === vocab.length - 1 ? 'not-allowed' : 'pointer',
            background: idx === vocab.length - 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
            color: 'white', fontSize: '1.5rem', fontWeight: 900,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >›</button>
      </div>

      <button onClick={onClose} style={{
        marginTop: '1.5rem', background: 'none', border: '2px solid rgba(255,255,255,0.3)',
        color: 'rgba(255,255,255,0.7)', padding: '0.5rem 1.5rem', borderRadius: 999,
        cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700,
      }}>
        {lang === 'ja' ? '× 閉じる' : '× Close'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// PRINTABLE WORKSHEET
// ─────────────────────────────────────────────────────
function WorksheetPrint({ worksheet, lang, dayNum, weekday }) {
  const handlePrint = () => {
    const win = window.open('', '_blank');
    const t = lang === 'ja';
    const jaFont = t ? '"Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic", "Meiryo",' : '';

    const renderField = (f) => {
      if (f.type === 'header') {
        return `<div class="section-header">${f.label[lang]}</div>`;
      }
      if (f.type === 'line') {
        return `<div class="field">
          <div class="field-label">${f.label[lang]}</div>
          <div class="write-line"></div>
        </div>`;
      }
      if (f.type === 'multiline') {
        return `<div class="field">
          <div class="field-label">${f.label[lang]}</div>
          <div class="write-line"></div>
          <div class="write-line"></div>
          <div class="write-line" style="margin-bottom:0"></div>
        </div>`;
      }
      if (f.type === 'choice') {
        const opts = f.options.map(o =>
          `<span class="choice-item"><span class="circle"></span> ${t ? '問題' : 'Problem'} ${o}</span>`
        ).join('');
        return `<div class="field">
          <div class="field-label">${f.label[lang]}</div>
          <div class="choice-row">${opts}</div>
        </div>`;
      }
      if (f.type === 'draw') {
        return `<div class="field">
          <div class="field-label">${f.label[lang]}</div>
          <div class="draw-box"><span class="draw-hint">✏️ ${t ? 'ここに絵を描こう' : 'Draw here'}</span></div>
        </div>`;
      }
      return '';
    };

    const html = `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${worksheet.title[lang]}</title>
<style>
  @page {
    size: A4 portrait;
    margin: 14mm 18mm 0 18mm;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    width: 100%;
    font-family: ${jaFont} "Helvetica Neue", Arial, sans-serif;
    font-size: 11pt;
    color: #1e293b;
    line-height: 1.5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    padding-bottom: 22mm;
  }

  /* ── HEADER ── */
  .ws-header {
    border-bottom: 3px solid #FF6B35;
    padding-bottom: 8pt;
    margin-bottom: 10pt;
  }
  .ws-header-top {
    display: table;
    width: 100%;
    margin-bottom: 4pt;
  }
  .ws-badge {
    display: table-cell;
    background: #FF6B35;
    color: white;
    border-radius: 5pt;
    padding: 2pt 8pt;
    font-weight: 800;
    font-size: 8pt;
    white-space: nowrap;
    vertical-align: middle;
    width: 1%;
  }
  .ws-emoji {
    display: table-cell;
    font-size: 18pt;
    padding: 0 8pt;
    vertical-align: middle;
    width: 1%;
  }
  .ws-title {
    display: table-cell;
    font-size: 15pt;
    font-weight: 900;
    color: #FF6B35;
    vertical-align: middle;
  }

  /* ── META (name / group / date) ── */
  .ws-meta {
    display: table;
    width: 100%;
    margin-bottom: 10pt;
    border-collapse: separate;
    border-spacing: 10pt 0;
  }
  .ws-meta td {
    display: table-cell;
    font-size: 10pt;
    color: #475569;
    font-weight: 700;
    white-space: nowrap;
    vertical-align: bottom;
    padding: 0;
  }
  .ws-meta .meta-line {
    display: inline-block;
    border-bottom: 1.2pt solid #94a3b8;
    width: 120pt;
    height: 18pt;
    vertical-align: bottom;
    margin-left: 4pt;
  }

  /* ── SECTION HEADER ── */
  .section-header {
    background: #FFF0E0;
    border-left: 5pt solid #FF6B35;
    padding: 5pt 10pt;
    font-weight: 900;
    font-size: 11pt;
    color: #c2410c;
    margin: 10pt 0 7pt 0;
    border-radius: 0 5pt 5pt 0;
    page-break-inside: avoid;
    break-inside: avoid;
    page-break-after: avoid;
    break-after: avoid;
  }

  /* ── FIELD ── */
  .field {
    margin-bottom: 8pt;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .field-label {
    font-weight: 700;
    font-size: 10pt;
    color: #334155;
    margin-bottom: 2pt;
  }
  .write-line {
    border-bottom: 1.2pt solid #64748b;
    width: 100%;
    height: 22pt;
    margin-bottom: 3pt;
  }

  /* ── CHOICE ── */
  .choice-row {
    display: table;
    width: 100%;
    margin-top: 6pt;
    border-collapse: separate;
    border-spacing: 6pt 0;
  }
  .choice-item {
    display: table-cell;
    font-weight: 800;
    font-size: 11pt;
    text-align: center;
    border: 1.5pt solid #cbd5e1;
    border-radius: 8pt;
    padding: 8pt 6pt;
  }
  .circle {
    display: inline-block;
    width: 14pt;
    height: 14pt;
    border: 1.5pt solid #64748b;
    border-radius: 50%;
    vertical-align: middle;
    margin-right: 5pt;
  }

  /* ── DRAW BOX ── */
  .draw-box {
    border: 2pt dashed #94a3b8;
    border-radius: 8pt;
    width: 100%;
    height: 120pt;
    margin-top: 4pt;
    text-align: center;
    display: table;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .draw-hint {
    display: table-cell;
    vertical-align: middle;
    color: #cbd5e1;
    font-size: 10pt;
  }

  /* ── FOOTER — fixed to bottom of every page ── */
  .ws-footer {
    position: fixed;
    bottom: 10mm;
    left: 18mm;
    right: 18mm;
    border-top: 1.2pt solid #e2e8f0;
    padding-top: 5pt;
    font-size: 8pt;
    color: #94a3b8;
    background: white;
  }
  .ws-footer table { width: 100%; border-collapse: collapse; }
  .ws-footer td { vertical-align: top; padding: 0; }
  .ws-footer td:last-child { text-align: right; }
  .ws-footer .footer-ja { display: block; font-size: 8.5pt; font-weight: 700; color: #475569; }
  .ws-footer .footer-en { display: block; font-size: 7.5pt; color: #94a3b8; margin-top: 1pt; }
</style>
</head>
<body>

<!-- footer: position:fixed renders at bottom of EVERY page in Chrome print -->
<div class="ws-footer">
  <table><tr>
    <td>
      <span class="footer-ja">琉球GLOCALキッズサマースクール２０２６</span>
      <span class="footer-en">Ryukyu GLOCAL Kids Summer School 2026</span>
    </td>
    <td style="text-align:right">
      <span class="footer-ja">${worksheet.title[lang]}</span>
      <span class="footer-en">DAY ${dayNum} · ${weekday}</span>
    </td>
  </tr></table>
</div>

<div class="ws-header">
  <div class="ws-header-top">
    <span class="ws-badge">DAY ${dayNum} &nbsp;·&nbsp; ${weekday}</span>
    <span class="ws-emoji">${worksheet.emoji}</span>
    <span class="ws-title">${worksheet.title[lang]}</span>
  </div>
</div>

<table class="ws-meta">
  <tr>
    <td>${t ? '名前' : 'Name'} <span class="meta-line"></span></td>
    <td>${t ? 'グループ' : 'Group'} <span class="meta-line"></span></td>
    <td>${t ? '日付' : 'Date'} <span class="meta-line" style="width:80pt"></span></td>
  </tr>
</table>

${worksheet.fields.map(renderField).join('\n')}

</body>
</html>`;

    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  return (
    <div style={{
      background: 'white', borderRadius: 16, padding: '1.5rem',
      border: '2px solid #F3F4F6', boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.8rem' }}>{worksheet.emoji}</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--navy)' }}>{worksheet.title[lang]}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, marginTop: 2 }}>
              {worksheet.fields.length} {lang === 'ja' ? '項目' : 'fields'} · A4
            </div>
          </div>
        </div>
        <button onClick={handlePrint} className="btn-summer" style={{ fontSize: '0.85rem', padding: '0.5rem 1.2rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          🖨️ {lang === 'ja' ? '印刷する' : 'Print'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// ACTIVITY CARD
// ─────────────────────────────────────────────────────
function ActivityCard({ activity, lang }) {
  const [expanded, setExpanded] = useState(false);
  const typeColor = ACTIVITY_TYPE_COLORS[activity.type] || '#64748b';
  const typeLabels = {
    en: { game: 'Game', lesson: 'Lesson', activity: 'Activity', share: 'Share Out', stamp: 'Stamp Time' },
    ja: { game: 'ゲーム', lesson: '授業', activity: 'アクティビティ', share: '発表', stamp: 'スタンプタイム' },
  };

  return (
    <div style={{
      background: 'white', borderRadius: 16,
      border: `2px solid ${expanded ? typeColor : '#F3F4F6'}`,
      overflow: 'hidden', transition: 'border-color 0.2s, box-shadow 0.2s',
      boxShadow: expanded ? `0 8px 32px ${typeColor}25` : '0 2px 8px rgba(0,0,0,0.04)',
      marginBottom: '0.75rem',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '1rem 1.25rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '1rem',
          background: expanded ? `${typeColor}10` : 'white',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: `${typeColor}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.3rem',
        }}>
          {activity.emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
            <span style={{
              background: typeColor, color: 'white',
              fontSize: '0.7rem', fontWeight: 800, padding: '1px 8px', borderRadius: 999,
              letterSpacing: '0.5px', textTransform: 'uppercase',
            }}>
              {typeLabels[lang][activity.type]}
            </span>
            <span style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 700 }}>
              {activity.time} · {activity.duration[lang]}
            </span>
          </div>
          <div style={{ fontWeight: 800, fontSize: 'clamp(0.9rem, 2.5vw, 1rem)', color: 'var(--navy)', lineHeight: 1.3 }}>
            {activity.title[lang]}
          </div>
        </div>
        <div style={{ fontSize: '1.2rem', color: typeColor, flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}>›</div>
      </div>

      {expanded && (
        <div style={{ padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${typeColor}20` }}>
          {activity.image && (
            <img
              src={activity.image}
              alt={activity.title[lang]}
              style={{ width: '100%', maxWidth: 640, display: 'block', margin: '1rem auto 0', borderRadius: 12, border: '1px solid #E5E7EB' }}
            />
          )}
          <div style={{
            marginTop: '1rem',
            whiteSpace: 'pre-line',
            fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
            lineHeight: 1.75,
            color: '#334155',
            fontWeight: 500,
          }}>
            {activity.content[lang]}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// MAIN CURRICULUM PAGE
// ─────────────────────────────────────────────────────
export default function CurriculumPage() {
  const { lang, user, api } = useApp();
  const [enrolledSlugs, setEnrolledSlugs] = useState(null); // null = loading

  // Auth itself is enforced by the ProtectedRoute wrapper in App.jsx —
  // this effect only figures out which programs to show once we have a user.
  useEffect(() => {
    if (!user) return;
    if (user.role === 'student') {
      // Always fetch fresh so admin program edits take effect immediately
      api('get', '/student/profile').then(res => {
        setEnrolledSlugs((res.data.programs || []).map(p => p.slug));
      }).catch(() => setEnrolledSlugs(user.programSlugs || []));
    } else {
      setEnrolledSlugs(null); // teachers/admins: show all
    }
  }, [user]);

  // Filter to only enrolled programs (students) or all (teachers/admins)
  const availablePrograms = (user?.role === 'student' && enrolledSlugs)
    ? PROGRAMS.filter(p => enrolledSlugs.includes(p.key === 'entrepreneurship' ? 'kids-entrepreneurship' : p.key))
    : PROGRAMS;

  const [selectedProgram, setSelectedProgram] = useState(0);
  const [selectedDay, setSelectedDay] = useState(0);
  const [activeSection, setActiveSection] = useState('vocab');
  const [showVocab, setShowVocab] = useState(false);

  // Show nothing while fetching enrollment (prevents AI Literacy flash for students)
  if (user?.role === 'student' && enrolledSlugs === null) return null;

  const program = availablePrograms[selectedProgram] || availablePrograms[0];
  const ACTIVE_DAYS = program.days;
  const ACTIVE_COLORS = program.colors;

  const day = ACTIVE_DAYS[selectedDay];
  const color = ACTIVE_COLORS[selectedDay];

  const hasVocab = (day.vocab?.length ?? 0) > 0;

  const hasMorning   = (day.morning?.activities?.length ?? 0) > 0;
  const hasAfternoon = (day.afternoon?.activities?.length ?? 0) > 0;
  const sections = [
    ...(hasVocab     ? [{ key: 'vocab',      emoji: '📚', en: 'Vocabulary',        ja: '単語カード' }] : []),
    ...(hasMorning   ? [{ key: 'morning',    emoji: '☀️', en: 'Morning Session',   ja: '午前の部'  }] : []),
    ...(hasAfternoon ? [{ key: 'afternoon',  emoji: '🌅', en: 'Afternoon Session', ja: '午後の部'  }] : []),
    { key: 'worksheets', emoji: '🖨️', en: 'Worksheets', ja: 'ワークシート' },
  ];

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      {showVocab && (
        <VocabFlashcard vocab={day.vocab} lang={lang} onClose={() => setShowVocab(false)} color={color} />
      )}

      {/* Program Switcher — hidden when only one program available */}
      {availablePrograms.length > 1 && <div style={{ background: '#1e293b', padding: '0.75rem 0' }}>
        <div className="container" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: 700, marginRight: 4 }}>
            {lang === 'ja' ? 'プログラム：' : 'Program:'}
          </span>
          {availablePrograms.map((p, i) => (
            <button
              key={p.key}
              onClick={() => { setSelectedProgram(i); setSelectedDay(0); const d = availablePrograms[i].days[0]; setActiveSection((d.vocab?.length ?? 0) > 0 ? 'vocab' : (d.morning?.activities?.length ?? 0) > 0 ? 'morning' : 'afternoon'); }}
              style={{
                border: 'none', cursor: 'pointer',
                borderRadius: 999,
                padding: '0.35rem 1rem',
                fontWeight: 800,
                fontSize: '0.8rem',
                background: selectedProgram === i ? p.headerGradient : 'rgba(255,255,255,0.1)',
                color: 'white',
                transition: 'all 0.2s',
              }}
            >
              {p.icon} {lang === 'ja' ? p.labelJa : p.labelEn}
            </button>
          ))}
        </div>
      </div>}

      {/* Day Selector Banner */}
      <div style={{ background: color, padding: 'clamp(1.5rem, 4vw, 2.5rem) 0 0' }}>
        <div className="container">
          <div style={{ color: 'white', marginBottom: '1rem' }}>
            <div style={{ fontSize: 'clamp(0.7rem, 2vw, 0.85rem)', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', opacity: 0.8 }}>
              {program.icon} {lang === 'ja' ? program.labelJa : program.labelEn} · 2026
            </div>
            <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)', fontWeight: 900, margin: '0.3rem 0 0.5rem', lineHeight: 1.2 }}>
              {lang === 'ja' ? 'カリキュラム' : 'Curriculum'}
            </h1>
          </div>

          {/* Day tabs */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: '1px' }}>
            {ACTIVE_DAYS.map((d, i) => (
              <button
                key={i}
                onClick={() => { setSelectedDay(i); setActiveSection((d.vocab?.length ?? 0) > 0 ? 'vocab' : (d.morning?.activities?.length ?? 0) > 0 ? 'morning' : 'afternoon'); }}
                style={{
                  border: 'none', cursor: 'pointer', borderRadius: '12px 12px 0 0',
                  padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(0.8rem, 2.5vw, 1.25rem)',
                  fontWeight: 800,
                  fontSize: 'clamp(0.75rem, 2vw, 0.9rem)',
                  transition: 'all 0.2s',
                  background: selectedDay === i ? 'white' : 'rgba(255,255,255,0.2)',
                  color: selectedDay === i ? '#1e293b' : 'white',
                  boxShadow: selectedDay === i ? '0 -4px 12px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                <span style={{ display: 'block', fontSize: 'clamp(0.6rem, 1.5vw, 0.7rem)', opacity: 0.7, marginBottom: 1 }}>
                  {lang === 'ja' ? `第${d.day}日` : `DAY ${d.day}`}
                </span>
                {d.weekday[lang]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Day Header */}
      <div style={{ background: 'white', borderBottom: '2px solid #F3F4F6', padding: 'clamp(1rem, 3vw, 1.5rem) 0' }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', fontWeight: 700, color: 'var(--orange)', marginBottom: '0.25rem' }}>
                {lang === 'ja' ? `第${day.day}日 — ${day.weekday[lang]}` : `DAY ${day.day} — ${day.weekday[lang]}`}
              </div>
              <h2 style={{ fontWeight: 900, fontSize: 'clamp(1rem, 3vw, 1.4rem)', color: 'var(--navy)', margin: '0 0 0.5rem', lineHeight: 1.3 }}>
                {day.theme[lang]}
              </h2>
              <p style={{ color: 'var(--muted)', fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', fontWeight: 600, margin: 0, lineHeight: 1.6 }}>
                🎯 {day.goal[lang]}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div style={{ background: 'white', borderBottom: '2px solid #F3F4F6', overflowX: 'auto' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: 4, minWidth: 'max-content' }}>
            {sections.map(s => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                style={{
                  border: 'none', cursor: 'pointer', padding: 'clamp(0.65rem, 2vw, 0.85rem) clamp(0.8rem, 2.5vw, 1.2rem)',
                  fontWeight: 800, fontSize: 'clamp(0.78rem, 2vw, 0.9rem)',
                  background: 'transparent',
                  color: activeSection === s.key ? 'var(--orange)' : 'var(--muted)',
                  borderBottom: activeSection === s.key ? '3px solid var(--orange)' : '3px solid transparent',
                  whiteSpace: 'nowrap', transition: 'all 0.15s',
                }}
              >
                {s.emoji} {s[lang]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ padding: 'clamp(1rem, 3vw, 2rem) var(--bs-gutter-x, 0.75rem)' }}>

        {/* ── VOCAB ── */}
        {activeSection === 'vocab' && hasVocab && (
          <div>
            <div style={{
              background: color, borderRadius: 20, padding: 'clamp(1.5rem, 4vw, 2.5rem)',
              textAlign: 'center', marginBottom: '1.5rem', color: 'white',
            }}>
              <div style={{ fontSize: 'clamp(2rem, 6vw, 3.5rem)', marginBottom: '0.75rem' }}>📚</div>
              <h3 style={{ fontWeight: 900, fontSize: 'clamp(1.1rem, 3.5vw, 1.6rem)', marginBottom: '0.5rem' }}>
                {lang === 'ja' ? '今日の重要ワード' : 'Key Vocabulary for Today'}
              </h3>
              <p style={{ opacity: 0.85, fontWeight: 600, marginBottom: '1.25rem', fontSize: 'clamp(0.85rem, 2.2vw, 1rem)' }}>
                {lang === 'ja'
                  ? `${day.vocab.length}つの単語 · カードをタップして意味を確認しよう`
                  : `${day.vocab.length} words · Tap the flashcard to see the definition`}
              </p>
              <button
                onClick={() => setShowVocab(true)}
                style={{
                  background: 'white', color: 'var(--orange)', border: 'none',
                  borderRadius: 999, padding: 'clamp(0.7rem, 2vw, 0.9rem) clamp(1.5rem, 4vw, 2.5rem)',
                  fontWeight: 900, fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                }}
              >
                {lang === 'ja' ? '📖 フラッシュカードで学ぶ' : '📖 Open Flashcards'}
              </button>
            </div>

            <div className="row g-3">
              {day.vocab.map((v, i) => (
                <div key={i} className="col-sm-6 col-lg-4">
                  <div
                    onClick={() => { setShowVocab(true); }}
                    style={{
                      background: 'white', borderRadius: 16,
                      border: '2px solid #F3F4F6', padding: '1.25rem',
                      cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'flex-start', gap: '1rem',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#F3F4F6'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <span style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', flexShrink: 0 }}>{v.emoji}</span>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 'clamp(1rem, 3vw, 1.2rem)', color: 'var(--navy)', marginBottom: '0.3rem' }}>
                        {v.word[lang]}
                      </div>
                      <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', color: 'var(--muted)', fontWeight: 600, lineHeight: 1.5 }}>
                        {v.def[lang]}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── MORNING ── */}
        {activeSection === 'morning' && (
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #FFF8E1, #FFFDE7)',
              borderRadius: 16, padding: '1rem 1.25rem', marginBottom: '1.25rem',
              border: '2px solid #FFD166', display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <span style={{ fontSize: '1.5rem' }}>☀️</span>
              <div style={{ fontWeight: 800, fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)', color: '#92400e' }}>
                {day.morning.time[lang]}
              </div>
            </div>
            {day.morning.activities.map((a, i) => (
              <ActivityCard key={i} activity={a} lang={lang} />
            ))}
          </div>
        )}

        {/* ── AFTERNOON ── */}
        {activeSection === 'afternoon' && (
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)',
              borderRadius: 16, padding: '1rem 1.25rem', marginBottom: '1.25rem',
              border: '2px solid #93C5FD', display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <span style={{ fontSize: '1.5rem' }}>🌅</span>
              <div style={{ fontWeight: 800, fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)', color: '#1e40af' }}>
                {day.afternoon.time[lang]}
              </div>
            </div>
            {day.afternoon.activities.map((a, i) => (
              <ActivityCard key={i} activity={a} lang={lang} />
            ))}
          </div>
        )}

        {/* ── WORKSHEETS ── */}
        {activeSection === 'worksheets' && (
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #F0FDF4, #DCFCE7)',
              borderRadius: 16, padding: '1rem 1.25rem', marginBottom: '1.25rem',
              border: '2px solid #86EFAC',
            }}>
              <div style={{ fontWeight: 800, fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)', color: '#166534' }}>
                🖨️ {lang === 'ja'
                  ? 'ワークシートをA4で印刷できます。テキストは全て表示されます。'
                  : 'All worksheets print to A4. All text is fully visible at any resolution.'}
              </div>
            </div>
            {(day.worksheets?.length ?? 0) === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)', fontWeight: 700 }}>
                {lang === 'ja' ? 'このデーのワークシートはありません' : 'No worksheets for this day yet.'}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {(day.worksheets || []).map(ws => (
                <WorksheetPrint key={ws.id} worksheet={ws} lang={lang} dayNum={day.day} weekday={day.weekday[lang]} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
